<script lang="ts">

  import { page } from "$app/state"
  import httpClient from "$lib/axios-client"
  import { Button } from "$lib/components/ui/button"
  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
  } from "$lib/components/ui/dialog"
  import { FieldLabel, FieldMessage } from "$lib/components/ui/field"
  import { Input } from "$lib/components/ui/input"
  import { Textarea } from "$lib/components/ui/textarea"
  import type { _response } from "$lib/data/type/_response"
  import { current } from "$lib/global-date"
  import { appStore } from "$lib/stores/app-store.svelte"
  import LoaderCircleIcon from "@lucide/svelte/icons/loader-circle"
  import { createForm } from "@tanstack/svelte-form"
  import { createMutation, useQueryClient } from "@tanstack/svelte-query"
  import { toast } from "svelte-sonner"
  import { z } from "zod/v4"
  import type { DateValue } from "@internationalized/date"

  const FormSchema = z.object({
    approval_status: z.string().default("0"),
    approval_at: z.custom<DateValue>().default(current()),
    approval_by_id: z.number().default(0),
    approval_by_name: z.string().default(""),
    approval_description: z.string().default(""),
    ids: z.record(z.string(), z.boolean()).default({}),
  })
  type FormData = z.infer<typeof FormSchema>
  const FormEmpty = FormSchema.parse({})

  let {
    open,
    setOpen,
    selects,
  }: {
    open: boolean
    setOpen: (value: boolean) => void
    selects: Record<string, boolean>
  } = $props()

  const prefix = $derived(page.params.prefix ?? "0")

  const queryClient = useQueryClient()

  const form = createForm(() => ({
    defaultValues: FormEmpty
  }))
  const formWatch = form.useSelector((state) => state.values)

  const mutation = createMutation(() => ({
    mutationFn: async (data: FormData) => {

      const response = await httpClient.post<_response<null>>(`/api/${prefix}/activity/approval-reals`, {
        ...data,
        approval_at: data.approval_at.format("yyyy-MM-dd HH:mm:ss"),
        ids: selects,
      })
      const responseData = response.data

      form.reset(form.state.values)
      Object.entries(responseData.errors ?? {}).forEach(([field, messages]) => {
        form.setFieldMeta(field as never, (previous) => ({
          ...previous,
          errorMap: { onServer: { message: messages[0] } },
        }))
      })

      if (response.status !== 200) return

      form.reset(FormEmpty)

      toast.success(responseData.message)
      setOpen(false)

      void queryClient.invalidateQueries({ queryKey: ["activity-approval-real"] })
    },
    onError: (error: Error) => {
      toast.error("Terjadi kesalahan", {
        description: error.message
      })
    }
  }))

</script>

<Dialog
  open={open}
  onOpenChange={setOpen}>
  <DialogContent
    class="relative w-sm"
    onOpenAutoFocus={(event) => event.preventDefault()}>
    <DialogHeader>
      <DialogTitle>Simpan</DialogTitle>
      <DialogDescription></DialogDescription>
    </DialogHeader>
    <form
      action="#"
      autocomplete="off"
      class="min-w-0 space-y-6"
      onsubmit={(event) => {
        event.preventDefault()
        form.setFieldValue("approval_status", "0")
        form.setFieldValue("approval_by_id", appStore.user?.id ?? 0)
        form.setFieldValue("approval_by_name", appStore.user?.name ?? "")
        form.setFieldValue("ids", selects)
        mutation.mutate(formWatch.current)
      }}>
      <div class="space-y-3">
        <form.Field name="approval_at">
          {#snippet children(field)}
            <div>
              <FieldLabel>Waktu</FieldLabel>
              <Input
                name={field.name}
                value={field.state.value.format("dd/MM/yyyy HH:mm")}
                readonly/>
              <FieldMessage name={field.name} form={form}/>
            </div>
          {/snippet}
        </form.Field>
        <form.Field name="approval_by_name">
          {#snippet children(field)}
            <div>
              <FieldLabel>Oleh</FieldLabel>
              <Input
                name={field.name}
                value={appStore.user?.name ?? ""}
                readonly/>
              <FieldMessage name={field.name} form={form}/>
            </div>
          {/snippet}
        </form.Field>
        <form.Field name="approval_description">
          {#snippet children(field)}
            <div>
              <FieldLabel>Keterangan</FieldLabel>
              <Textarea
                name={field.name}
                value={field.state.value}
                oninput={(event) => field.handleChange(event.currentTarget.value)}/>
              <FieldMessage name={field.name} form={form}/>
            </div>
          {/snippet}
        </form.Field>
      </div>
      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onclick={() => setOpen(false)}>
          Batal
        </Button>
        <Button
          type="submit"
          class="group"
          data-loading={mutation.isPending}
          disabled={mutation.isPending}>
          <LoaderCircleIcon class="invisible absolute animate-spin group-data-[loading=true]:visible"/>
          Simpan
        </Button>
      </DialogFooter>
    </form>
  </DialogContent>
</Dialog>
